// src/pages/AdminMemberManagementPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import axios from '../api/axios';
import { useUser } from '../context/UserContext';
import * as XLSX from 'xlsx';

// --- 가짜 데이터 (나중에 이 모든 데이터를 백엔드 API로부터 받아옵니다) ---
const loggedInStaff = { name: 'Alice', role: 'PRESIDENT', branch: 'NCCU' }; // 'PRESIDENT', 'All'로 바꿔서 테스트해보세요.

// 초기 트랜잭션(수입/지출 통합)
const initialTransactionsByBranch = {
  NCCU: [
    { id: 1, type: 'expense', date: '2025-07-25', item: 'BBQ Party Supplies', amount: 3500, submittedBy: 'Alice', status: 'Pending' },
    { id: 4, type: 'revenue', date: '2025-07-20', item: 'Membership Fee', amount: 12000, submittedBy: 'System' },
  ],
  TAIPEI: [
    { id: 2, type: 'expense', date: '2025-07-26', item: 'Venue Rental Deposit', amount: 2000, submittedBy: 'Peter', status: 'Reimbursed' },
    { id: 5, type: 'revenue', date: '2025-07-22', item: 'Event Ticket', amount: 4500, submittedBy: 'System' },
  ],
  NTU: [
    { id: 3, type: 'expense', date: '2025-07-27', item: 'Scavenger Hunt Prizes', amount: 1500, submittedBy: 'Diana', status: 'Pending' },
  ],
};
// --------------------------------------------------------------------

// --- 상세 정보 팝업(Modal) 컴포넌트 ---
const DetailModal = ({ user, onClose, onDeleteMembership, onDeleteApplication, membershipPricing, branchFilter, isApplication = false, currentUser }) => {
  // Helper function to get branches (same as Staff Info)
  const getActiveBranches = (member) => {
    if (member?.role === 'ADMIN') {
      return ['NCCU', 'NTU', 'TAIPEI'];
    }
    
    const set = new Set();
    const str = (member?.membership || member?.branch || member?.selectedBranch || '').trim();
    if (str) set.add(str);
    if (Array.isArray(member?.memberships)) {
      for (const um of member.memberships) {
        const b = (um?.branchName || '').trim();
        const status = (um?.status || '').toUpperCase();
        if (b && (status === 'ACTIVE' || status === '')) set.add(b);
      }
    }
    return Array.from(set);
  };
  
  const branches = getActiveBranches(user);
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
        <div className="bg-white p-6 sm:p-8 rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl sm:text-2xl font-bold">{user?.name || user?.userName || 'Unknown User'}</h2>
                <button 
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                >
                    ×
                </button>
            </div>
            
            <div className="space-y-3 text-sm">
                <div className="grid grid-cols-1 gap-3">
                    {/* Email */}
                    <div className="p-3 bg-gray-50 rounded">
                <p><strong>Email:</strong> {user?.email || user?.userEmail || 'N/A'}</p>
            </div>
                    
                    {/* Role */}
                    <div className="p-3 bg-gray-50 rounded">
                        <p><strong>Role:</strong> {user?.role || 'MEMBER'}</p>
                    </div>
                    
                    {/* Branch */}
                    <div className="p-3 bg-gray-50 rounded">
                        <p><strong>Branch:</strong> {branches.join(', ') || 'N/A'}</p>
                    </div>
                    
                    {/* Student ID */}
                    <div className="p-3 bg-gray-50 rounded">
                        <p><strong>Student ID:</strong> {user?.studentId || 'N/A'}</p>
                    </div>
                    
                    {/* Phone */}
                    <div className="p-3 bg-gray-50 rounded">
                        <p><strong>Phone:</strong> {user?.phone || 'N/A'}</p>
                    </div>
                    
                    {/* Major */}
                    <div className="p-3 bg-gray-50 rounded">
                        <p><strong>Major:</strong> {user?.major || 'N/A'}</p>
                    </div>
                    
                    {/* Nationality - N/A means Taiwan */}
                    <div className="p-3 bg-gray-50 rounded">
                        <p><strong>Nationality:</strong> {(() => {
                            // Pending Approval users have country field, All Members have nationality field
                            const nationalityValue = user?.nationality || user?.country;
                            return nationalityValue === 'N/A' || !nationalityValue ? 'Taiwan' : nationalityValue;
                        })()}</p>
                    </div>
                    
                    {/* Payment */}
                    <div className="p-3 bg-gray-50 rounded">
                        <p><strong>Payment:</strong> {user?.paymentMethod ? `${user.paymentMethod === 'transfer' ? `Transfer (${user.bankLast5 || 'N/A'})` : 'Cash'} - ${user?.amount === 0 ? 'FREE' : (user?.amount || 'N/A')} ${user?.amount === 0 ? '' : 'NTD'}` : 'N/A'}</p>
                    </div>
                </div>
            </div>
            
            <div className="mt-6 space-y-2">
                <button onClick={onClose} className="w-full py-2 bg-gray-200 rounded hover:bg-gray-300">Close</button>
                {isApplication ? (
                    onDeleteApplication && (currentUser?.role === 'ADMIN' || currentUser?.role === 'PRESIDENT' || currentUser?.role === 'LEADER') && (
                        <button 
                            onClick={() => {
                                if (window.confirm(`Are you sure you want to delete ${user?.userName || 'this application'}?`)) {
                                    onDeleteApplication(user.id);
                                }
                            }} 
                            className="w-full py-2 bg-red-500 text-white rounded hover:bg-red-600"
                        >
                            Delete Application
                        </button>
                    )
                ) : (
                    onDeleteMembership && (currentUser?.role === 'PRESIDENT' || currentUser?.role === 'ADMIN') && (
                    <button 
                        onClick={() => {
                            if (window.confirm(`Are you sure you want to delete ${user?.name || user?.userName || 'this user'}'s membership?`)) {
                                    onDeleteMembership(user.id, branchFilter);
                            }
                        }} 
                        className="w-full py-2 bg-red-500 text-white rounded hover:bg-red-600"
                    >
                        Delete Membership
                    </button>
                    )
                )}
            </div>
        </div>
    </div>
);
};

export default function AdminMemberManagementPage() {
  const { user } = useUser();
  const [activeTab, setActiveTab] = useState('approvals');
  const [tickets, setTickets] = useState([]);
  const [ticketSearch, setTicketSearch] = useState('');
  const [ticketStatusFilter, setTicketStatusFilter] = useState('ALL');
  const [ticketNationalityFilter, setTicketNationalityFilter] = useState('ALL');
  const [ticketPaymentFilter, setTicketPaymentFilter] = useState('ALL');
  const [ticketViewMode, setTicketViewMode] = useState('list'); // 'list' or 'grouped'
  const [ticketCurrentPage, setTicketCurrentPage] = useState(1);
  const [ticketPageSize, setTicketPageSize] = useState(20);
  const [branchFilter, setBranchFilter] = useState(user?.branch === 'All' ? 'NCCU' : user?.branch || 'NCCU');
  const [selectedUser, setSelectedUser] = useState(null); // 모달에 보여줄 사용자 정보
  const [applications, setApplications] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [showPendingInfo, setShowPendingInfo] = useState(false);
  
  // All Members 탭 필터링 상태 추가
  const [memberSearch, setMemberSearch] = useState('');
  const [memberNationalityFilter, setMemberNationalityFilter] = useState('ALL');
  const [memberSortKey, setMemberSortKey] = useState('name');
  const [memberCurrentPage, setMemberCurrentPage] = useState(1);
  const [memberPageSize, setMemberPageSize] = useState(20);
  
  // Pending Approval 탭 필터링 상태 추가
  const [approvalSearch, setApprovalSearch] = useState('');
  const [approvalNationalityFilter, setApprovalNationalityFilter] = useState('ALL');
  const [approvalSortKey, setApprovalSortKey] = useState('userName');
  const [approvalCurrentPage, setApprovalCurrentPage] = useState(1);
  const [approvalPageSize, setApprovalPageSize] = useState(20);
  
  // 회계 트랜잭션 상태 (지부별) - membership fee 승인 시 revenue 추가에 사용
  const [transactionsByBranch, setTransactionsByBranch] = useState(initialTransactionsByBranch);
  // Receipt 미리보기 모달용
  const [viewReceiptUrl, setViewReceiptUrl] = useState('');

  const availableBranches = ['NCCU', 'NTU', 'TAIPEI'];

  // 화면 크기 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1200); // iPad Pro까지 카드 뷰 사용
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ✅ 실제 API에서 데이터 가져오기
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (activeTab === 'approvals') {
          const response = await axios.get('/api/admin/membership-applications');
          setApplications(response.data);
        } else if (activeTab === 'all_members') {
          const response = await axios.get('/api/admin/users?sort=name');
          
          // ✅ Admin 제외하여 Dashboard와 통일
          const filteredMembers = response.data.filter(member => member.role !== 'ADMIN');
          
          // 최근 승인된 멤버 정보가 있다면 해당 멤버 정보를 보강
          const recentlyApproved = localStorage.getItem('recentlyApprovedMember');
          if (recentlyApproved) {
            try {
              const approvedInfo = JSON.parse(recentlyApproved);
              const enhancedMembers = filteredMembers.map(member => {
                if (member.email === approvedInfo.email) {
                  return {
                    ...member,
                    studentId: approvedInfo.studentId,
                    major: approvedInfo.major,
                    country: approvedInfo.country,
                    paymentMethod: approvedInfo.paymentMethod,
                    amount: approvedInfo.amount,
                    bankLast5: approvedInfo.bankLast5,
                    phone: approvedInfo.phone,
                    professionalStatus: approvedInfo.professionalStatus
                  };
                }
                return member;
              });
              setMembers(enhancedMembers);
              // 정보 사용 후 삭제
              localStorage.removeItem('recentlyApprovedMember');
            } catch (parseError) {
          setMembers(filteredMembers);
            }
          } else {
          setMembers(filteredMembers);
          }
        } else if (activeTab === 'tickets') {
          // Load ticket registrations
          try {
            const response = await axios.get('/api/admin/tickets');
            setTickets(response.data);
          } catch (error) {
            // 개발 중이므로 목업 데이터 사용 (지부 정보 포함)
            setTickets([
              {
                id: 1,
                eventId: 'EVT001',
                eventTitle: 'Outing Event',
                eventBranch: branchFilter, // 현재 선택된 지부
                userEmail: 'john.doe@example.com',
                userName: 'John Doe',
                status: 'PENDING',
                notes: 'First time attendee',
                createdAt: new Date().toISOString()
              },
              {
                id: 2,
                eventId: 'EVT002',
                eventTitle: 'Networking Party',
                eventBranch: branchFilter, // 현재 선택된 지부
                userEmail: 'jane.smith@example.com',
                userName: 'Jane Smith',
                status: 'PAID',
                notes: 'VIP member',
                createdAt: new Date(Date.now() - 86400000).toISOString()
              }
            ]);
          }
        }
      } catch (error) {
        // Silent fail
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeTab, branchFilter]);

  // Pending Approval 탭용 필터링 로직
  const baseApplications = applications.filter(app => 
    app.selectedBranch === branchFilter && app.status === 'payment_pending'
  );
  
  // 신청 국적 목록 추출 (필터링용)
  const approvalNationalities = useMemo(() => {
    const set = new Set();
    for (const app of baseApplications) {
      const nationalityValue = app?.nationality || app?.country;
      if (nationalityValue && nationalityValue.trim()) {
        set.add(nationalityValue.trim());
      }
    }
    const sortedList = Array.from(set).sort();
    // Add Taiwan if not already present (for N/A cases)
    if (!sortedList.includes('Taiwan')) {
      sortedList.push('Taiwan');
      sortedList.sort();
    }
    return ['ALL', ...sortedList];
  }, [baseApplications]);
  
  // 신청 필터링 및 정렬
  const filteredApplications = useMemo(() => {
    const searchQuery = approvalSearch.trim().toLowerCase();
    let list = [...baseApplications];
    
    // 검색 필터
    if (searchQuery) {
      list = list.filter(app =>
        (app.userName || '').toLowerCase().includes(searchQuery) ||
        (app.userEmail || app.email || '').toLowerCase().includes(searchQuery) ||
        (app.studentId || '').toLowerCase().includes(searchQuery)
      );
    }
    
    // 국적 필터
    if (approvalNationalityFilter !== 'ALL') {
      list = list.filter(app => {
        const nationalityValue = app?.nationality || app?.country;
        const displayNationality = nationalityValue === 'N/A' || !nationalityValue ? 'Taiwan' : nationalityValue;
        if (approvalNationalityFilter === 'LOCAL') return displayNationality === 'Taiwan';
        if (approvalNationalityFilter === 'INTERNATIONAL') return displayNationality !== 'Taiwan';
        return displayNationality === approvalNationalityFilter;
      });
    }
    
    // 정렬
    const sortBy = (value) => (value == null ? '' : String(value));
    if (approvalSortKey === 'userName') {
      list.sort((a, b) => sortBy(a.userName).localeCompare(sortBy(b.userName)));
    } else if (approvalSortKey === 'userName_desc') {
      list.sort((a, b) => sortBy(b.userName).localeCompare(sortBy(a.userName)));
    } else if (approvalSortKey === 'email') {
      list.sort((a, b) => sortBy(a.userEmail || a.email).localeCompare(sortBy(b.userEmail || b.email)));
    } else if (approvalSortKey === 'email_desc') {
      list.sort((a, b) => sortBy(b.userEmail || b.email).localeCompare(sortBy(a.userEmail || a.email)));
    } else if (approvalSortKey === 'nationality') {
      list.sort((a, b) => {
        const na = (a?.nationality || a?.country || 'Taiwan');
        const nb = (b?.nationality || b?.country || 'Taiwan');
        return sortBy(na).localeCompare(sortBy(nb));
      });
    } else if (approvalSortKey === 'nationality_desc') {
      list.sort((a, b) => {
        const na = (a?.nationality || a?.country || 'Taiwan');
        const nb = (b?.nationality || b?.country || 'Taiwan');
        return sortBy(nb).localeCompare(sortBy(na));
      });
    } else if (approvalSortKey === 'paymentMethod') {
      list.sort((a, b) => sortBy(a.paymentMethod).localeCompare(sortBy(b.paymentMethod)));
    } else if (approvalSortKey === 'paymentMethod_desc') {
      list.sort((a, b) => sortBy(b.paymentMethod).localeCompare(sortBy(a.paymentMethod)));
    } else if (approvalSortKey === 'amount') {
      list.sort((a, b) => (a.amount || 0) - (b.amount || 0));
    } else if (approvalSortKey === 'amount_desc') {
      list.sort((a, b) => (b.amount || 0) - (a.amount || 0));
    } else if (approvalSortKey === 'createdAt') {
      list.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
    } else if (approvalSortKey === 'createdAt_desc') {
      list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    }
    
    return list;
  }, [baseApplications, approvalSearch, approvalNationalityFilter, approvalSortKey]);
  
  // 신청 페이지네이션
  const approvalTotalPages = Math.ceil(filteredApplications.length / approvalPageSize);
  const approvalStartIndex = (approvalCurrentPage - 1) * approvalPageSize;
  const approvalEndIndex = approvalStartIndex + approvalPageSize;
  const paginatedApplications = filteredApplications.slice(approvalStartIndex, approvalEndIndex);
  
  // 신청 국적별 통계 계산 (필터 영향 X: baseApplications 기준)
  const approvalNationalityStats = useMemo(() => {
    const stats = { local: 0, international: 0, total: baseApplications.length };
    const taiwanKeywords = ['taiwan', 'tw', '대만', '타이완', 'republic of china'];
    
    baseApplications.forEach(app => {
      const country = (app.nationality || app.country || '').toLowerCase();
      
      // N/A means Taiwan (LOCAL STUDENT choice during signup)
      const baseValue = app.nationality || app.country;
      const isLocal = (!baseValue || baseValue === 'N/A') 
        ? true // N/A means Taiwan (local)
        : taiwanKeywords.some(keyword => country.includes(keyword));
        
      if (isLocal) {
        stats.local++;
      } else {
        stats.international++;
      }
    });
    
    return stats;
  }, [baseApplications]);
  
  // All Members 탭용 필터링 로직
  const allMembers = Array.isArray(members) ? members : [];
  
  // Staff Info처럼 지부별 필터링 추가 (클라이언트 사이드)
  const getActiveBranches = (member) => {
    // ✅ ADMIN만 특별 처리: 빈 배열 반환 (특정 지부에 속하지 않음)
    if (member?.role === 'ADMIN') {
      return []; // ADMIN은 지부별 목록에서 제외됨
    }
    
    const set = new Set();
    const str = (member?.membership || '').trim();
    if (str) set.add(str);
    if (Array.isArray(member?.memberships)) {
      for (const um of member.memberships) {
        const b = (um?.branchName || '').trim();
        const status = (um?.status || '').toUpperCase();
        if (b && (status === 'ACTIVE' || status === '')) set.add(b);
      }
    }
    return Array.from(set);
  };
  
  // 지부별 필터링된 멤버 (Staff Info와 동일한 로직)
  // ✅ Admin 제외: Application Management에서는 Admin을 지부별 목록에서 제외
  const baseMembers = allMembers.filter(member => {
    // Admin은 지부별 목록에서 제외
    if (member?.role === 'ADMIN') {
      return false;
    }
    const branches = getActiveBranches(member);
    return branches.includes(branchFilter);
  });
  
  // 국적 목록 추출 (필터링용) - nationality 또는 country 필드 사용
  const memberNationalities = useMemo(() => {
    const set = new Set();
    for (const member of baseMembers) {
      const nationalityValue = member?.nationality || member?.country;
      if (nationalityValue && nationalityValue.trim()) {
        const displayNationality = nationalityValue === 'N/A' || !nationalityValue ? 'Taiwan' : nationalityValue;
        set.add(displayNationality);
      }
    }
    const sortedList = Array.from(set).sort();
    // Add Taiwan if not already present (for N/A cases)
    if (!sortedList.includes('Taiwan')) {
      sortedList.push('Taiwan');
      sortedList.sort();
    }
    return ['ALL', ...sortedList];
  }, [baseMembers]);
  
  // 멤버 필터링 및 정렬
  const filteredMembers = useMemo(() => {
    const searchQuery = memberSearch.trim().toLowerCase();
    let list = [...baseMembers];
    
    // 검색 필터
    if (searchQuery) {
      list = list.filter(member =>
        (member.name || '').toLowerCase().includes(searchQuery) ||
        (member.email || '').toLowerCase().includes(searchQuery) ||
        (member.studentId || '').toLowerCase().includes(searchQuery)
      );
    }
    
    // 국적 필터 - nationality 또는 country 필드 사용
    if (memberNationalityFilter !== 'ALL') {
      list = list.filter(member => {
        const nationalityValue = member?.nationality || member?.country;
        const displayNationality = nationalityValue === 'N/A' || !nationalityValue ? 'Taiwan' : nationalityValue;
        if (memberNationalityFilter === 'INTERNATIONAL') {
          return displayNationality !== 'Taiwan';
        }
        return displayNationality === memberNationalityFilter;
      });
    }
    
    // 정렬
    const sortBy = (value) => (value == null ? '' : String(value));
    if (memberSortKey === 'name') {
      list.sort((a, b) => sortBy(a.name).localeCompare(sortBy(b.name)));
    } else if (memberSortKey === 'name_desc') {
      list.sort((a, b) => sortBy(b.name).localeCompare(sortBy(a.name)));
    } else if (memberSortKey === 'email') {
      list.sort((a, b) => sortBy(a.email).localeCompare(sortBy(b.email)));
    } else if (memberSortKey === 'email_desc') {
      list.sort((a, b) => sortBy(b.email).localeCompare(sortBy(a.email)));
    } else if (memberSortKey === 'joinedCount') {
      list.sort((a, b) => (b.joinedCount || 0) - (a.joinedCount || 0));
    } else if (memberSortKey === 'joinedCount_desc') {
      list.sort((a, b) => (a.joinedCount || 0) - (b.joinedCount || 0));
    } else if (memberSortKey === 'nationality') {
      list.sort((a, b) => sortBy(a.nationality || 'Taiwan').localeCompare(sortBy(b.nationality || 'Taiwan')));
    } else if (memberSortKey === 'nationality_desc') {
      list.sort((a, b) => sortBy(b.nationality || 'Taiwan').localeCompare(sortBy(a.nationality || 'Taiwan')));
    } else if (memberSortKey === 'joinDate') {
      list.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA; // 최신순 (내림차순)
      });
    } else if (memberSortKey === 'joinDateOldest') {
      list.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateA - dateB; // 오래된순 (오름차순)
      });
    }
    
    return list;
  }, [baseMembers, memberSearch, memberNationalityFilter, memberSortKey]);
  
  // 페이지네이션
  const memberTotalPages = Math.ceil(filteredMembers.length / memberPageSize);
  const memberStartIndex = (memberCurrentPage - 1) * memberPageSize;
  const memberEndIndex = memberStartIndex + memberPageSize;
  const paginatedMembers = filteredMembers.slice(memberStartIndex, memberEndIndex);
  
  // 국적별 통계 계산 (필터 영향 X: baseMembers 기준)
  const nationalityStats = useMemo(() => {
    const stats = { local: 0, international: 0, total: baseMembers.length };
    const taiwanKeywords = ['taiwan', 'tw', '대만', '타이완', 'republic of china'];
    
    baseMembers.forEach(member => {
      const nationalityValue = member?.nationality || member?.country;
      const nationality = (nationalityValue || '').toLowerCase();
      
      // N/A means Taiwan (LOCAL STUDENT choice during signup)
      const isLocal = (!nationalityValue || nationalityValue === 'N/A') 
        ? true // N/A means Taiwan (local)
        : taiwanKeywords.some(keyword => nationality.includes(keyword));
        
      if (isLocal) {
        stats.local++;
      } else {
        stats.international++;
      }
    });
    
    return stats;
  }, [baseMembers]);

  // ✅ 멤버십 삭제 함수
  const handleDeleteMembership = async (userId, branchName) => {
    try {
      await axios.delete(`/api/admin/users/memberships?userId=${userId}&branchName=${branchName}`);
      alert('Membership deleted successfully');
      // 목록 새로고침 - All Members는 /api/admin/users 사용
      const response = await axios.get('/api/admin/users?sort=name');
      setMembers(response.data);
      setSelectedUser(null);
    } catch (error) {
      alert('Failed to delete membership');
    }
  };

  // ✅ 체크박스 관련 함수들
  const handleSelectMember = (memberId) => {
    const newSelected = new Set(selectedMembers);
    if (newSelected.has(memberId)) {
      newSelected.delete(memberId);
    } else {
      newSelected.add(memberId);
    }
    setSelectedMembers(newSelected);
    setSelectAll(newSelected.size === filteredMembers.length);
  };
  
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedMembers(new Set());
    } else {
      setSelectedMembers(new Set(filteredMembers.map(m => m.id)));
    }
    setSelectAll(!selectAll);
  };
  
  const handleBulkDelete = async () => {
    if (selectedMembers.size === 0) {
      alert('Please select members to delete.');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedMembers.size} membership(s)?`)) {
      return;
    }
    
    try {
      const deletePromises = Array.from(selectedMembers).map(memberId => {
        const member = members.find(m => m.id === memberId);
        if (member) {
          const branchName = member?.branch || member?.selectedBranch || branchFilter;
          return axios.delete(`/api/admin/users/memberships?userId=${memberId}&branchName=${branchName}`);
        }
        return Promise.resolve();
      });
      
      await Promise.all(deletePromises);
      alert(`${selectedMembers.size} membership(s) deleted successfully`);
      
      // 목록 새로고침
      const response = await axios.get('/api/admin/users?sort=name');
      setMembers(response.data);
      setSelectedMembers(new Set());
      setSelectAll(false);
    } catch (error) {
      alert('Failed to delete some memberships');
    }
  };
  
  // ✅ Pending Approvals 체크박스 관련 함수들
  const handleSelectApplication = (applicationId) => {
    const newSelected = new Set(selectedApplications);
    if (newSelected.has(applicationId)) {
      newSelected.delete(applicationId);
    } else {
      newSelected.add(applicationId);
    }
    setSelectedApplications(newSelected);
    setSelectAllApplications(newSelected.size === filteredApplications.length);
  };
  
  const handleSelectAllApplications = () => {
    if (selectAllApplications) {
      setSelectedApplications(new Set());
    } else {
      setSelectedApplications(new Set(filteredApplications.map(a => a.id)));
    }
    setSelectAllApplications(!selectAllApplications);
  };
  
  // ✅ 개별 신청서 삭제 함수 (reject API 사용)
  const handleDeleteApplication = async (applicationId) => {
    if (!window.confirm('Are you sure you want to delete this application?')) {
      return;
    }
    
    try {
      // reject API를 사용하여 신청서를 거부 (삭제 효과)
      await axios.post(`/api/admin/applications/reject?applicationId=${applicationId}`);
      alert('Application deleted successfully');
      
      // 목록 새로고침
      const response = await axios.get('/api/admin/membership-applications');
      setApplications(response.data);
      setSelectedUser(null);
    } catch (error) {
      alert('Failed to delete application');
    }
  };
  
  const handleBulkDeleteApplications = async () => {
    if (selectedApplications.size === 0) {
      alert('Please select applications to delete.');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedApplications.size} application(s)?`)) {
      return;
    }
    
    try {
      const deletePromises = Array.from(selectedApplications).map(applicationId => {
        return axios.post(`/api/admin/applications/reject?applicationId=${applicationId}`);
      });
      
      await Promise.all(deletePromises);
      alert(`${selectedApplications.size} application(s) deleted successfully`);
      
      // 목록 새로고침
      const response = await axios.get('/api/admin/membership-applications');
      setApplications(response.data);
      setSelectedApplications(new Set());
      setSelectAllApplications(false);
    } catch (error) {
      alert('Failed to delete some applications');
    }
  };



  // 동적 멤버십 가격 상태
  const [membershipPricing, setMembershipPricing] = useState({ currentPrice: 900, earlyBirdPrice: 800, regularPrice: 900 });
  const membershipFeeNTD = membershipPricing.currentPrice; // 멤버십 회비 기준 금액
  const displayMembershipFee = membershipFeeNTD === 0 ? 'FREE' : `NT$${membershipFeeNTD.toLocaleString()}`;
  
  // 체크박스 상태 관리
  const [selectedMembers, setSelectedMembers] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Pending Approvals 체크박스 상태 관리
  const [selectedApplications, setSelectedApplications] = useState(new Set());
  const [selectAllApplications, setSelectAllApplications] = useState(false);

  // GA 팀인지 확인하는 함수
  const isGATeamMember = (user) => {
    if (!user?.affiliation) return false;
    return user.affiliation === 'General Affairs';
  };
  
  // 멤버십 가격 정보 가져오기 (현재 활성화된 이벤트 기준)
  useEffect(() => {
    const fetchMembershipPricing = async () => {
      try {
        // 먼저 현재 활성화된 Regular SLAM Meet 이벤트 찾기
        const eventsResponse = await axios.get(`/api/events?branch=${branchFilter}`);
        const activeEvents = eventsResponse.data.filter(event => 
          event.eventType === 'REGULAR_MEET' && 
          !event.archived && 
          new Date(event.eventDateTime) > new Date()
        );
        
        if (activeEvents.length > 0) {
          // 가장 가까운 이벤트 선택
          const nearestEvent = activeEvents.sort((a, b) => 
            new Date(a.eventDateTime) - new Date(b.eventDateTime)
          )[0];
          
          // 해당 이벤트의 가격 정보 가져오기
          const pricingResponse = await axios.get(`/api/events/${nearestEvent.id}/membership-pricing?branch=${branchFilter}`);
          setMembershipPricing(pricingResponse.data);
        } else {
          // 활성화된 이벤트가 없으면 기존 API 사용
          const response = await axios.get(`/api/memberships/pricing?branch=${branchFilter}`);
          setMembershipPricing(response.data);
        }
      } catch (error) {
        // 기본값 유지
      }
    };
    
    fetchMembershipPricing();
  }, [branchFilter]);

  // ✅ XLSX 내보내기 함수
  const handleExportXLSX = (members, branch) => {
    const headers = [
      'Timeline',
      'Name',
      'Email', 
      'Student ID',
      'Major',
      'Nationality',
      'Phone',
      'Payment Method',
      'Payment Amount',
      'Branch',
      'Events Joined',
      'Professional Status'
    ];
    
    const data = members.map(member => [
      member.createdAt ? new Date(member.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Unknown',
      member.name || member.userName || '',
      member.email || member.userEmail || '',
      member.studentId || '',
      member.major || '',
      (() => {
          const nationalityValue = member?.nationality || member?.country;
          return nationalityValue === 'N/A' || !nationalityValue ? 'Taiwan' : nationalityValue;
      })(),
      member.phone || '',
      member.paymentMethod ? (member.paymentMethod === 'transfer' ? `Transfer (${member.bankLast5})` : 'Cash') : 'N/A',
      member.amount || 'N/A',
      member.selectedBranch || member.membership || member.branch || branch,
      member.joinedCount || 0,
      member.professionalStatus || ''
    ]);
    
    // 워크시트 생성
    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
    
    // 워크북 생성
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Members');
    
    // 파일 다운로드
    XLSX.writeFile(wb, `SLAM_${branch}_Members_${new Date().toISOString().split('T')[0]}.xlsx`);
  };


  // 티켓 관련 함수들
  const handleSaveTicket = async (ticketData) => {
    try {
      const response = await axios.post('/api/admin/tickets', ticketData);
      setTickets(prev => [response.data, ...prev]);
    } catch (error) {
      // 개발 중이므로 목업 데이터 추가
      const newTicket = {
        id: Date.now(),
        ...ticketData,
        createdAt: new Date().toISOString()
      };
      setTickets(prev => [newTicket, ...prev]);
    }
  };

  const handleUpdateTicketStatus = async (ticketId, newStatus) => {
    try {
      await axios.put(`/api/admin/tickets/${ticketId}/status`, { status: newStatus });
      setTickets(prev => prev.map(ticket => 
        ticket.id === ticketId ? { ...ticket, status: newStatus } : ticket
      ));
    } catch (error) {
      // 개발 중이므로 로컬 상태 업데이트
      setTickets(prev => prev.map(ticket => 
        ticket.id === ticketId ? { ...ticket, status: newStatus } : ticket
      ));
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to delete this ticket?')) return;
    
    try {
      await axios.delete(`/api/admin/tickets/${ticketId}`);
      setTickets(prev => prev.filter(ticket => ticket.id !== ticketId));
    } catch (error) {
      // 개발 중이므로 로컬 상태 업데이트
      setTickets(prev => prev.filter(ticket => ticket.id !== ticketId));
    }
  };

  // 멤버 클릭 시 상세 정보 가져오기
  const handleMemberClick = async (member) => {
    try {
      // 멤버십 신청 정보에서 해당 유저의 상세 정보 찾기
      const memberApplication = applications.find(app => app.userEmail === member.email);
      if (memberApplication) {
        setSelectedUser(memberApplication);
      } else {
        // 신청 정보가 없으면 기본 멤버 정보 사용
        setSelectedUser(member);
      }
    } catch (error) {
      setSelectedUser(member);
    }
  };

  // 티켓 필터링 로직
  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = (ticket.eventId && String(ticket.eventId).toLowerCase().includes(ticketSearch.toLowerCase())) ||
                         (ticket.userEmail && ticket.userEmail.toLowerCase().includes(ticketSearch.toLowerCase())) ||
                         (ticket.eventTitle && ticket.eventTitle.toLowerCase().includes(ticketSearch.toLowerCase())) ||
                         (ticket.userName && ticket.userName.toLowerCase().includes(ticketSearch.toLowerCase()));
    const matchesStatus = ticketStatusFilter === 'ALL' || ticket.status === ticketStatusFilter;
    // ✅ Branch 필터링 수정: eventBranch가 현재 선택된 branchFilter와 일치하는 것만
    const matchesBranch = ticket.eventBranch === branchFilter;
    
    // 국적 필터링 - 나라별로 직접 비교
    const matchesNationality = ticketNationalityFilter === 'ALL' || ticket.nationality === ticketNationalityFilter;
    
    // 결제 방법 필터링
    const matchesPayment = ticketPaymentFilter === 'ALL' || ticket.paymentMethod === ticketPaymentFilter;
    
    // Past Event 필터링: 이벤트 날짜가 지났고 SPECIAL_EVENT 타입인 경우 숨김
    const now = new Date();
    const eventDate = ticket.eventDate ? new Date(ticket.eventDate) : null;
    const isPastEvent = eventDate && eventDate < now && ticket.eventType === 'SPECIAL_EVENT';
    const matchesPastEventFilter = !isPastEvent; // Past Event가 아닌 것만 표시
    
    return matchesSearch && matchesStatus && matchesBranch && matchesNationality && matchesPayment && matchesPastEventFilter;
  });

  // 티켓 정렬 상태
  const [ticketSortKey, setTicketSortKey] = useState('createdAt_desc');

  // 티켓 정렬 적용
  const sortedTickets = useMemo(() => {
    const list = [...filteredTickets];
    const sortBy = (value) => (value == null ? '' : String(value));
    if (ticketSortKey === 'name') {
      list.sort((a, b) => sortBy(a.userName).localeCompare(sortBy(b.userName)));
    } else if (ticketSortKey === 'name_desc') {
      list.sort((a, b) => sortBy(b.userName).localeCompare(sortBy(a.userName)));
    } else if (ticketSortKey === 'nationality') {
      list.sort((a, b) => sortBy(a.nationality || 'Taiwan').localeCompare(sortBy(b.nationality || 'Taiwan')));
    } else if (ticketSortKey === 'nationality_desc') {
      list.sort((a, b) => sortBy(b.nationality || 'Taiwan').localeCompare(sortBy(a.nationality || 'Taiwan')));
    } else if (ticketSortKey === 'status') {
      list.sort((a, b) => sortBy(a.status).localeCompare(sortBy(b.status)));
    } else if (ticketSortKey === 'status_desc') {
      list.sort((a, b) => sortBy(b.status).localeCompare(sortBy(a.status)));
    } else if (ticketSortKey === 'createdAt') {
      list.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
    } else if (ticketSortKey === 'createdAt_desc') {
      list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    }
    return list;
  }, [filteredTickets, ticketSortKey]);

  // 이벤트별로 그룹핑된 티켓 데이터
  const groupedTickets = useMemo(() => {
    const groups = {};
    sortedTickets.forEach(ticket => {
      const eventKey = `${ticket.eventId}-${ticket.eventTitle || 'Unknown Event'}`;
      if (!groups[eventKey]) {
        groups[eventKey] = {
          eventId: ticket.eventId,
          eventTitle: ticket.eventTitle || 'Unknown Event',
          eventBranch: ticket.eventBranch,
          tickets: []
        };
      }
      groups[eventKey].tickets.push(ticket);
    });
    return Object.values(groups);
  }, [sortedTickets]);

  // 티켓 페이지네이션
  const ticketTotalPages = Math.ceil(sortedTickets.length / ticketPageSize);
  const ticketStartIndex = (ticketCurrentPage - 1) * ticketPageSize;
  const paginatedTickets = sortedTickets.slice(ticketStartIndex, ticketStartIndex + ticketPageSize);

  // 티켓 통계 - ✅ filteredTickets 기준으로 변경 (Branch 필터 반영)
  const ticketStats = {
    total: filteredTickets.length,
    pending: filteredTickets.filter(t => t.status === 'PENDING').length,
    paid: filteredTickets.filter(t => t.status === 'PAID').length
  };

  // 티켓 Local/International 통계 - ✅ filteredTickets 기준으로 변경 (Branch 필터 반영)
  const ticketNationalityStats = useMemo(() => {
    const stats = { local: 0, international: 0, total: filteredTickets.length };
    const taiwanKeywords = ['taiwan', 'tw', '대만', '타이완', 'republic of china'];
    filteredTickets.forEach(t => {
      const baseValue = t?.nationality; // 티켓은 nationality만 사용
      const lower = (baseValue || '').toLowerCase();
      const isLocal = (!baseValue || baseValue === 'N/A') ? true : taiwanKeywords.some(k => lower.includes(k));
      if (isLocal) stats.local++; else stats.international++;
    });
    return stats;
  }, [filteredTickets]);

  // ✅ 신청서 승인 함수 (Reject 제거) + 회계 수입 반영
  const handleApproveApplication = async (application) => {
    try {
      await axios.post(`/api/admin/applications/approve?applicationId=${application.id}`);
      alert('Application approved successfully');
      
      // 승인된 멤버 정보를 localStorage에 임시 저장 (상세 정보 전달용)
      const approvedMemberInfo = {
        email: application.userEmail || application.email,
        studentId: application.studentId,
        major: application.major,
        country: application.country,
        paymentMethod: application.paymentMethod,
        amount: application.amount,
        bankLast5: application.bankLast5,
        selectedBranch: application.selectedBranch,
        userName: application.userName,
        phone: application.phone,
        professionalStatus: application.professionalStatus
      };
      localStorage.setItem('recentlyApprovedMember', JSON.stringify(approvedMemberInfo));
      
      // 목록 새로고침
      const response = await axios.get('/api/admin/membership-applications');
      setApplications(response.data);

      // 승인 시 해당 지부에 Revenue 자동 반영
      const branch = application.selectedBranch || branchFilter;
      setTransactionsByBranch(prev => {
        const updated = { ...prev };
        const list = [...(updated[branch] || [])];
        list.push({
          id: Date.now(),
          type: 'revenue',
          date: new Date().toISOString().slice(0,10),
          item: 'Membership Fee',
          amount: application.amount || membershipFeeNTD,
          submittedBy: application.userName || 'System',
        });
        updated[branch] = list;
        return updated;
      });
      
      // All Members 탭으로 자동 전환
      setActiveTab('all_members');
    } catch (error) {
      alert('Failed to approve application: ' + (error.response?.data || error.message));
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
        {selectedUser && activeTab !== 'tickets' && <DetailModal 
          user={selectedUser} 
          onClose={() => setSelectedUser(null)} 
          onDeleteMembership={handleDeleteMembership}
          onDeleteApplication={handleDeleteApplication}
          membershipPricing={membershipPricing} 
          branchFilter={branchFilter}
          isApplication={activeTab === 'approvals'}
          currentUser={user}
        />}
        {viewReceiptUrl && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" onClick={() => setViewReceiptUrl('')}>
            <div className="bg-white p-4 rounded-lg shadow-2xl max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-semibold">Receipt</h3>
                <button onClick={() => setViewReceiptUrl('')} className="text-gray-600 hover:text-gray-900">✕</button>
              </div>
              <div className="max-h-[75vh] overflow-auto">
                <img src={viewReceiptUrl} alt="Receipt" className="w-full h-auto object-contain" />
              </div>
            </div>
          </div>
        )}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Application Management</h1>
          <p className="text-gray-600 text-sm sm:text-base">Manage applications, view member lists, and track finances for each branch</p>
        </div>

      {/* Branch Tabs - Enhanced UI (동일하게 Staff Info 스타일 적용) */}
      <div className={`mb-6 ${isMobile ? '' : 'overflow-x-auto'}`}>
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl border-2 border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Filter by Branch:</span>
          </div>
          <div className={`flex gap-3 ${isMobile ? 'flex-wrap' : 'min-w-max'}`}>
            {availableBranches.map(branch => (
              <button
                key={branch}
                onClick={() => setBranchFilter(branch)}
                className={
                  (branchFilter === branch
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg scale-105 border-2 border-blue-500 '
                    : 'bg-white text-gray-700 hover:bg-gray-50 hover:shadow-md border-2 border-gray-300 hover:border-blue-300 ')
                  + `px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${isMobile ? 'flex-1 min-w-0' : 'whitespace-nowrap'}`
                }
                title={`Show ${branch} branch`}
              >
                <div className="flex items-center justify-center gap-2">
                  <span className="font-semibold">{branch}</span>
                  {branchFilter === branch && (
                    <span className="text-xs">✓</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>


      <div className="border-b border-gray-200">
        <nav className={`-mb-px flex gap-2 sm:gap-6 ${isMobile ? 'flex-col sm:flex-row' : 'overflow-x-auto'}`}>
          <button onClick={() => setActiveTab('approvals')} className={`py-2 sm:py-4 px-1 border-b-2 font-medium text-sm sm:text-base ${isMobile ? 'w-full text-center' : 'whitespace-nowrap'} ${activeTab === 'approvals' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:border-gray-300'}`}>Membership Pending</button>
          <button onClick={() => setActiveTab('all_members')} className={`py-2 sm:py-4 px-1 border-b-2 font-medium text-sm sm:text-base ${isMobile ? 'w-full text-center' : 'whitespace-nowrap'} ${activeTab === 'all_members' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:border-gray-300'}`}>Membership List</button>
          <button onClick={() => setActiveTab('tickets')} className={`py-2 sm:py-4 px-1 border-b-2 font-medium text-sm sm:text-base ${isMobile ? 'w-full text-center' : 'whitespace-nowrap'} ${activeTab === 'tickets' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:border-gray-300'}`}>Ticket Registration</button>
        </nav>
      </div>

      <div className="mt-8">
        {activeTab === 'approvals' && (
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-3">
                <h2 className="text-xl font-semibold">Pending Approvals for {branchFilter} ({filteredApplications.length})</h2>
                <button
                  type="button"
                  onClick={() => setShowPendingInfo(!showPendingInfo)}
                  className="text-blue-500 hover:text-blue-700 text-sm"
                  title="Click for information about pending management"
                >
                  ℹ️
                </button>
              </div>
              <div className="flex gap-2">
                {selectedApplications.size > 0 && (
                  <button 
                    onClick={handleBulkDeleteApplications}
                    className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 font-medium"
                  >
                    Delete Selected ({selectedApplications.size})
                  </button>
                )}
                <button 
                  onClick={() => handleExportXLSX(filteredApplications, `${branchFilter}_Pending`)}
                  className="bg-emerald-500 text-white px-4 py-2 rounded-lg hover:bg-emerald-600 font-medium transition-all duration-200 focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 shadow-sm"
                >
                  📊 Export XLSX
                </button>
              </div>
            </div>
            
            {/* Pending 관리 설명 */}
            {showPendingInfo && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-2">📋 Pending Management Guidelines:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>After each event is completed and feedback is collected, <strong>LEADER level and above</strong> should clear the pending list</li>
                    <li>This allows users to apply for the next event with updated pricing</li>
                    <li>Use "Delete Selected" to bulk remove pending applications</li>
                    <li>Individual applications can be approved or deleted using the action buttons</li>
                  </ul>
                </div>
              </div>
            )}
            
            {/* 필터링 및 통계 섹션 */}
            <div className="mb-6 space-y-4">
              {/* 통계 카드 */}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <h3 className="text-sm font-medium text-yellow-800">Total Pending</h3>
                  <p className="text-xl font-bold text-yellow-900">{approvalNationalityStats.total}</p>
                </div>
                <button type="button" onClick={() => setApprovalNationalityFilter(approvalNationalityFilter === 'LOCAL' ? 'ALL' : 'LOCAL')} className={`bg-green-50 p-3 rounded-lg text-left border ${approvalNationalityFilter === 'LOCAL' ? 'border-green-400' : 'border-transparent'}`}>
                  <h3 className="text-sm font-medium text-green-800">Local Applications</h3>
                  <p className="text-xl font-bold text-green-900">{approvalNationalityStats.local}</p>
                </button>
                <button type="button" onClick={() => setApprovalNationalityFilter(approvalNationalityFilter === 'INTERNATIONAL' ? 'ALL' : 'INTERNATIONAL')} className={`bg-purple-50 p-3 rounded-lg text-left border ${approvalNationalityFilter === 'INTERNATIONAL' ? 'border-purple-400' : 'border-transparent'}`}>
                  <h3 className="text-sm font-medium text-purple-800">International Apps</h3>
                  <p className="text-xl font-bold text-purple-900">{approvalNationalityStats.international}</p>
                </button>
                <div className="bg-orange-50 p-3 rounded-lg">
                  <h3 className="text-sm font-medium text-orange-800">Local:Int Ratio</h3>
                  <p className="text-xl font-bold text-orange-900">
                    {approvalNationalityStats.total > 0 
                      ? `${Math.round((approvalNationalityStats.local / approvalNationalityStats.total) * 100)}:${Math.round((approvalNationalityStats.international / approvalNationalityStats.total) * 100)}`
                      : '0:0'
                    }
                  </p>
                </div>
              </div>
              
              {/* 필터 컨트롤 */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-4 bg-gray-50 rounded-lg">
                <input
                  value={approvalSearch}
                  onChange={e => setApprovalSearch(e.target.value)}
                  placeholder="Search: Name, Email, Student ID"
                  className="border rounded px-3 py-2"
                />
                <select 
                  value={approvalNationalityFilter} 
                  onChange={e => setApprovalNationalityFilter(e.target.value)} 
                  className="border rounded px-3 py-2"
                >
                  {approvalNationalities.map(nationality => (
                    <option key={nationality} value={nationality}>
                      {nationality === 'ALL' ? 'All Nationalities' : nationality}
                    </option>
                  ))}
                </select>
                <select 
                  value={approvalSortKey} 
                  onChange={e => setApprovalSortKey(e.target.value)} 
                  className="border rounded px-3 py-2"
                >
                  <option value="createdAt_desc">Sort: Applied Date (Newest)</option>
                  <option value="createdAt">Sort: Applied Date (Oldest)</option>
                  <option value="userName">Sort: Name</option>
                  <option value="email">Sort: Email</option>
                  <option value="country">Sort: Nationality</option>
                  <option value="paymentMethod">Sort: Payment Method</option>
                </select>
                <select 
                  value={approvalPageSize} 
                  onChange={e => {
                    setApprovalPageSize(Number(e.target.value));
                    setApprovalCurrentPage(1);
                  }} 
                  className="border rounded px-3 py-2"
                >
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>
            </div>
            {loading ? (
              <p className="text-center text-gray-500">Loading applications...</p>
            ) : isMobile ? (
              // Mobile Card View for Approvals
              <div className="space-y-4">
                {paginatedApplications.map(app => (
                  <div key={app.id} onClick={() => setSelectedUser(app)} className="bg-gray-50 p-4 rounded-lg border cursor-pointer hover:bg-gray-100">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-gray-500">
                            {app.createdAt ? new Date(app.createdAt).toLocaleDateString('en-CA') : 'N/A'}
                          </span>
                          <h3 className="font-semibold text-gray-900">{app.userName || 'Unknown'}</h3>
                        </div>
                        <p className="text-sm text-gray-600">{app.paymentMethod === 'transfer' ? `Transfer (${app.bankLast5})` : 'Cash'}</p>
                        <p className="text-sm font-semibold text-green-600">{app.amount === 0 ? 'FREE' : (app.amount ? `${app.amount} NTD` : 'N/A')}</p>
                      </div>
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">{app.status}</span>
                    </div>
                    <button 
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        handleApproveApplication(app); 
                      }} 
                      className="bg-green-500 text-white text-xs font-bold py-1 px-3 rounded-full hover:bg-green-600"
                    >
                      Approve
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              // Desktop Table View for Approvals
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                          <input type="checkbox" checked={selectAllApplications} onChange={handleSelectAllApplications} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 cursor-pointer hover:bg-gray-100" onClick={() => setApprovalSortKey(approvalSortKey === 'createdAt' ? 'createdAt_desc' : 'createdAt')}>
                          Applied Date {approvalSortKey === 'createdAt' ? '↑' : approvalSortKey === 'createdAt_desc' ? '↓' : ''}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 cursor-pointer hover:bg-gray-100" onClick={() => setApprovalSortKey(approvalSortKey === 'userName' ? 'userName_desc' : 'userName')}>
                          Name {approvalSortKey === 'userName' ? '↑' : approvalSortKey === 'userName_desc' ? '↓' : ''}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 cursor-pointer hover:bg-gray-100" onClick={() => setApprovalSortKey(approvalSortKey === 'email' ? 'email_desc' : 'email')}>
                          Email {approvalSortKey === 'email' ? '↑' : approvalSortKey === 'email_desc' ? '↓' : ''}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 cursor-pointer hover:bg-gray-100" onClick={() => setApprovalSortKey(approvalSortKey === 'nationality' ? 'nationality_desc' : 'nationality')}>
                          Nationality {approvalSortKey === 'nationality' ? '↑' : approvalSortKey === 'nationality_desc' ? '↓' : ''}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 cursor-pointer hover:bg-gray-100" onClick={() => setApprovalSortKey(approvalSortKey === 'paymentMethod' ? 'paymentMethod_desc' : 'paymentMethod')}>
                          Payment {approvalSortKey === 'paymentMethod' ? '↑' : approvalSortKey === 'paymentMethod_desc' ? '↓' : ''}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 cursor-pointer hover:bg-gray-100" onClick={() => setApprovalSortKey(approvalSortKey === 'amount' ? 'amount_desc' : 'amount')}>
                          Amount {approvalSortKey === 'amount' ? '↑' : approvalSortKey === 'amount_desc' ? '↓' : ''}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedApplications.map(app => (
                            <tr key={app.id} className="hover:bg-gray-50">
                                <td className="px-4 py-2">
                                  <input 
                                    type="checkbox" 
                                    checked={selectedApplications.has(app.id)}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleSelectApplication(app.id);
                                    }}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                </td>
                                <td className="px-4 py-2 cursor-pointer" onClick={() => setSelectedUser(app)}>
                                  <span className="text-xs text-gray-700 whitespace-nowrap">
                                    {app.createdAt ? new Date(app.createdAt).toLocaleDateString('en-CA') : 'N/A'}
                                  </span>
                                </td>
                                <td className="px-4 py-2 cursor-pointer" onClick={() => setSelectedUser(app)}>{app.userName || 'Unknown'}</td>
                                <td className="px-4 py-2 cursor-pointer" onClick={() => setSelectedUser(app)}>{app.userEmail || app.email || 'N/A'}</td>
                                <td className="px-4 py-2 cursor-pointer" onClick={() => setSelectedUser(app)}>{app.country === 'N/A' || !app.country ? 'Taiwan' : app.country}</td>
                                <td className="px-4 py-2 cursor-pointer" onClick={() => setSelectedUser(app)}><span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">{app.status}</span></td>
                                <td className="px-4 py-2 cursor-pointer" onClick={() => setSelectedUser(app)}>{app.paymentMethod === 'transfer' ? `Transfer (${app.bankLast5})` : 'Cash'}</td>
                                <td className="px-4 py-2 cursor-pointer" onClick={() => setSelectedUser(app)}>{app.amount === 0 ? 'FREE' : (app.amount ? `${app.amount} NTD` : 'N/A')}</td>
                                <td className="px-4 py-2">
                                  <div className="flex gap-2">
                                  <button onClick={(e) => { e.stopPropagation(); handleApproveApplication(app); }} className="bg-green-500 text-white text-xs font-bold py-1 px-3 rounded-full hover:bg-green-600">Approve</button>
                                    {(user?.role === 'ADMIN' || user?.role === 'PRESIDENT' || user?.role === 'LEADER') && (
                                      <button onClick={(e) => { e.stopPropagation(); handleDeleteApplication(app.id); }} className="bg-red-500 text-white text-xs font-bold py-1 px-3 rounded-full hover:bg-red-600">Delete</button>
                                    )}
                                  </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
              </div>
            )}
            
            {/* 페이지네이션 */}
            {approvalTotalPages > 1 && (
              <div className="mt-6 flex justify-center items-center space-x-2">
                <button
                  onClick={() => setApprovalCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={approvalCurrentPage === 1}
                  className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  Previous
                </button>
                
                <span className="text-sm text-gray-600">
                  Page {approvalCurrentPage} of {approvalTotalPages} ({filteredApplications.length} pending applications)
                </span>
                
                <button
                  onClick={() => setApprovalCurrentPage(prev => Math.min(approvalTotalPages, prev + 1))}
                  disabled={approvalCurrentPage === approvalTotalPages}
                  className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
        {activeTab === 'all_members' && (
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">All Active Members in {branchFilter} ({filteredMembers.length})</h2>
              <div className="flex gap-2">
                {selectedMembers.size > 0 && (
                  <button 
                    onClick={handleBulkDelete}
                    className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 font-medium"
                  >
                    Delete Selected ({selectedMembers.size})
                  </button>
                )}
                <button 
                  onClick={() => handleExportXLSX(filteredMembers, branchFilter)}
                  className="bg-emerald-500 text-white px-4 py-2 rounded-lg hover:bg-emerald-600 font-medium transition-all duration-200 focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 shadow-sm"
                >
                  Export XLSX
                </button>
              </div>
            </div>
            
            {/* 필터링 및 통계 섹션 */}
            <div className="mb-6 space-y-4">
              {/* 통계 카드 */}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <h3 className="text-sm font-medium text-blue-800">Total Members</h3>
                  <p className="text-xl font-bold text-blue-900">{nationalityStats.total}</p>
                </div>
                <button type="button" onClick={() => setMemberNationalityFilter(memberNationalityFilter === 'Taiwan' ? 'ALL' : 'Taiwan')} className={`bg-green-50 p-3 rounded-lg text-left border ${memberNationalityFilter === 'Taiwan' ? 'border-green-400' : 'border-transparent'}`}>
                  <h3 className="text-sm font-medium text-green-800">Local (Taiwan)</h3>
                  <p className="text-xl font-bold text-green-900">{nationalityStats.local}</p>
                </button>
                <button type="button" onClick={() => setMemberNationalityFilter(memberNationalityFilter === 'INTERNATIONAL' ? 'ALL' : 'INTERNATIONAL')} className={`bg-purple-50 p-3 rounded-lg text-left border ${memberNationalityFilter === 'INTERNATIONAL' ? 'border-purple-400' : 'border-transparent'}`}>
                  <h3 className="text-sm font-medium text-purple-800">International</h3>
                  <p className="text-xl font-bold text-purple-900">{nationalityStats.international}</p>
                </button>
                <div className="bg-orange-50 p-3 rounded-lg">
                  <h3 className="text-sm font-medium text-orange-800">Local:Int Ratio</h3>
                  <p className="text-xl font-bold text-orange-900">
                    {nationalityStats.total > 0 
                      ? `${Math.round((nationalityStats.local / nationalityStats.total) * 100)}:${Math.round((nationalityStats.international / nationalityStats.total) * 100)}`
                      : '0:0'
                    }
                  </p>
                </div>
              </div>
              
              {/* 필터 컨트롤 */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-4 bg-gray-50 rounded-lg">
                <input
                  value={memberSearch}
                  onChange={e => setMemberSearch(e.target.value)}
                  placeholder="Search: Name, Email, Student ID"
                  className="border rounded px-3 py-2"
                />
                <select 
                  value={memberNationalityFilter} 
                  onChange={e => setMemberNationalityFilter(e.target.value)} 
                  className="border rounded px-3 py-2"
                >
                  {memberNationalities.map(nationality => (
                    <option key={nationality} value={nationality}>
                      {nationality === 'ALL' ? 'All Nationalities' : nationality}
                    </option>
                  ))}
                </select>
                <select 
                  value={memberSortKey} 
                  onChange={e => setMemberSortKey(e.target.value)} 
                  className="border rounded px-3 py-2"
                >
                  <option value="name">Sort: Name</option>
                  <option value="email">Sort: Email</option>
                  <option value="country">Sort: Nationality</option>
                  <option value="joinedCount">Sort: Events Joined</option>
                  <option value="joinDate">Sort: Join Date (Newest)</option>
                  <option value="joinDateOldest">Sort: Join Date (Oldest)</option>
                </select>
                <select 
                  value={memberPageSize} 
                  onChange={e => {
                    setMemberPageSize(Number(e.target.value));
                    setMemberCurrentPage(1);
                  }} 
                  className="border rounded px-3 py-2"
                >
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>
            </div>
            {loading ? (
              <p className="text-center text-gray-500">Loading members...</p>
            ) : isMobile ? (
              // Mobile Card View for Members
              <div className="space-y-4">
                {paginatedMembers.map(member => (
                  <div key={member.id} onClick={() => handleMemberClick(member)} className="bg-gray-50 p-4 rounded-lg border cursor-pointer hover:bg-gray-100">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-gray-500">
                            {member.createdAt ? new Date(member.createdAt).toLocaleDateString('en-CA') : 'N/A'}
                          </span>
                        </div>
                        <h3 className="font-semibold text-gray-900">{member.name}</h3>
                        <p className="text-sm text-gray-600">{member.email}</p>
                        <p className="text-sm text-gray-500">{(() => {
                            if (member?.role === 'ADMIN') {
                              return ['NCCU', 'NTU', 'TAIPEI'].join(', ');
                            }
                            const set = new Set();
                            const str = (member?.membership || member?.branch || '').trim();
                            if (str) set.add(str);
                            if (Array.isArray(member?.memberships)) {
                              for (const um of member.memberships) {
                                const b = (um?.branchName || '').trim();
                                const status = (um?.status || '').toUpperCase();
                                if (b && (status === 'ACTIVE' || status === '')) set.add(b);
                              }
                            }
                            return Array.from(set).join(', ') || 'No membership';
                          })()}</p>
                        <p className="text-xs text-gray-500">Joined: {member.joinedCount ?? 0}</p>
                      </div>
                    </div>
                    {(user?.role === 'ADMIN' || user?.role === 'PRESIDENT') && (
                      <button 
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          handleDeleteMembership(member.id, branchFilter); 
                        }} 
                        className="bg-red-500 text-white text-xs font-bold py-1 px-3 rounded-full hover:bg-red-600"
                      >
                        Delete Membership
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              // Desktop Table View for Members
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                          <input 
                            type="checkbox" 
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 cursor-pointer hover:bg-gray-100" onClick={() => setMemberSortKey(memberSortKey === 'joinDate' ? 'joinDateOldest' : 'joinDate')}>
                          Applied Date {memberSortKey === 'joinDate' ? '↑' : memberSortKey === 'joinDateOldest' ? '↓' : ''}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 cursor-pointer hover:bg-gray-100" onClick={() => setMemberSortKey(memberSortKey === 'name' ? 'name_desc' : 'name')}>
                          Name {memberSortKey === 'name' ? '↑' : memberSortKey === 'name_desc' ? '↓' : ''}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 cursor-pointer hover:bg-gray-100" onClick={() => setMemberSortKey(memberSortKey === 'email' ? 'email_desc' : 'email')}>
                          Email {memberSortKey === 'email' ? '↑' : memberSortKey === 'email_desc' ? '↓' : ''}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 cursor-pointer hover:bg-gray-100" onClick={() => setMemberSortKey(memberSortKey === 'nationality' ? 'nationality_desc' : 'nationality')}>
                          Nationality {memberSortKey === 'nationality' ? '↑' : memberSortKey === 'nationality_desc' ? '↓' : ''}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Membership</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Dietary Preferences</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 cursor-pointer hover:bg-gray-100" onClick={() => setMemberSortKey(memberSortKey === 'joinedCount' ? 'joinedCount_desc' : 'joinedCount')}>
                          Events Joined {memberSortKey === 'joinedCount' ? '↑' : memberSortKey === 'joinedCount_desc' ? '↓' : ''}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedMembers.map(member => (
                            <tr key={member.id} className="hover:bg-gray-50">
                                <td className="px-4 py-2">
                                  <input 
                                    type="checkbox" 
                                    checked={selectedMembers.has(member.id)}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleSelectMember(member.id);
                                    }}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                </td>
                                <td className="px-4 py-2 cursor-pointer" onClick={() => handleMemberClick(member)}>
                                  <span className="text-xs text-gray-700 whitespace-nowrap">
                                    {member.createdAt ? new Date(member.createdAt).toLocaleDateString('en-CA') : 'N/A'}
                                  </span>
                                </td>
                                <td className="px-4 py-2 cursor-pointer" onClick={() => handleMemberClick(member)}>{member.name}</td>
                                <td className="px-4 py-2 cursor-pointer" onClick={() => handleMemberClick(member)}>{member.email}</td>
                                <td className="px-4 py-2 cursor-pointer" onClick={() => handleMemberClick(member)}>{(() => {
                                  const nationalityValue = member?.nationality || member?.country;
                                  return nationalityValue === 'N/A' || !nationalityValue ? 'Taiwan' : nationalityValue;
                                })()}</td>
                                <td className="px-4 py-2 cursor-pointer" onClick={() => handleMemberClick(member)}>{(() => {
                                  if (member?.role === 'ADMIN') {
                                    return ['NCCU', 'NTU', 'TAIPEI'].join(', ');
                                  }
                                  const set = new Set();
                                  const str = (member?.membership || member?.branch || '').trim();
                                  if (str) set.add(str);
                                  if (Array.isArray(member?.memberships)) {
                                    for (const um of member.memberships) {
                                      const b = (um?.branchName || '').trim();
                                      const status = (um?.status || '').toUpperCase();
                                      if (b && (status === 'ACTIVE' || status === '')) set.add(b);
                                    }
                                  }
                                  return Array.from(set).join(', ') || 'No membership';
                                })()}</td>
                                <td className="px-4 py-2 cursor-pointer" onClick={() => handleMemberClick(member)}>
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    member.foodAllergies === 'None' || !member.foodAllergies ? 'bg-gray-100 text-gray-800' :
                                    member.foodAllergies === 'Vegetarian' ? 'bg-green-100 text-green-800' :
                                    member.foodAllergies === 'Vegan' ? 'bg-purple-100 text-purple-800' :
                                    'bg-orange-100 text-orange-800'
                                  }`}>
                                    {member.foodAllergies || 'None'}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-xs text-gray-500 cursor-pointer" onClick={() => handleMemberClick(member)}>{member.joinedCount ?? 0}</td>
                                <td className="px-4 py-2">
                                  {(user?.role === 'ADMIN' || user?.role === 'PRESIDENT') && (
                                    <button onClick={(e) => { e.stopPropagation(); handleDeleteMembership(member.id, branchFilter); }} className="bg-red-500 text-white text-xs font-bold py-1 px-3 rounded-full hover:bg-red-600">Delete Membership</button>
                                  )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
              </div>
            )}
            
            {/* 페이지네이션 */}
            {memberTotalPages > 1 && (
              <div className="mt-6 flex justify-center items-center space-x-2">
                <button
                  onClick={() => setMemberCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={memberCurrentPage === 1}
                  className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  Previous
                </button>
                
                <span className="text-sm text-gray-600">
                  Page {memberCurrentPage} of {memberTotalPages} ({filteredMembers.length} total members)
                </span>
                
                <button
                  onClick={() => setMemberCurrentPage(prev => Math.min(memberTotalPages, prev + 1))}
                  disabled={memberCurrentPage === memberTotalPages}
                  className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
        {/* Accounting 탭 제거됨 - AdminAccountingPage.jsx로 이동 */}
        {activeTab === 'tickets' && (
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Single Event Ticket Registrations ({filteredTickets.length})</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setTicketViewMode(ticketViewMode === 'grouped' ? 'list' : 'grouped')}
                  className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 font-medium text-sm"
                >
                  {ticketViewMode === 'grouped' ? 'List View' : 'Group by Event'}
                </button>
              </div>
            </div>
            
            {/* 통계 카드 - 모바일/PC 반응형 */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
              <div className="bg-white p-3 rounded-lg shadow-md">
                <h3 className="text-xs font-medium text-gray-500">Total Tickets</h3>
                <p className="text-xl font-bold text-gray-900">{ticketStats.total}</p>
              </div>
              <div className="bg-white p-3 rounded-lg shadow-md">
                <h3 className="text-xs font-medium text-gray-500">Pending</h3>
                <p className="text-xl font-bold text-yellow-600">{ticketStats.pending}</p>
              </div>
              <div className="bg-white p-3 rounded-lg shadow-md">
                <h3 className="text-xs font-medium text-gray-500">Paid</h3>
                <p className="text-xl font-bold text-green-600">{ticketStats.paid}</p>
              </div>
              <button type="button" onClick={() => setTicketNationalityFilter(ticketNationalityFilter === 'Taiwan' ? 'ALL' : 'Taiwan')} className={`bg-green-50 p-3 rounded-lg shadow-md text-left border ${ticketNationalityFilter === 'Taiwan' ? 'border-green-400' : 'border-transparent'}`}>
                <h3 className="text-xs font-medium text-green-800">Local (Taiwan)</h3>
                <p className="text-xl font-bold text-green-900">{ticketNationalityStats.local}</p>
              </button>
              <button type="button" onClick={() => setTicketNationalityFilter(ticketNationalityFilter === 'INTERNATIONAL' ? 'ALL' : 'INTERNATIONAL')} className={`bg-purple-50 p-3 rounded-lg shadow-md text-left border ${ticketNationalityFilter === 'INTERNATIONAL' ? 'border-purple-400' : 'border-transparent'}`}>
                <h3 className="text-xs font-medium text-purple-800">International</h3>
                <p className="text-xl font-bold text-purple-900">{ticketNationalityStats.international}</p>
              </button>
            </div>

            {/* 필터 및 검색 - 모바일/PC 반응형 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
              <div className="lg:col-span-2">
                <label htmlFor="ticket-search" className="block text-xs font-medium text-gray-700 mb-1">Search</label>
                <input
                  type="text"
                  id="ticket-search"
                  value={ticketSearch}
                  onChange={(e) => setTicketSearch(e.target.value)}
                  placeholder="Search by Event ID, Email, or Name..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              <div>
                <label htmlFor="ticket-status-filter" className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                <select
                  id="ticket-status-filter"
                  value={ticketStatusFilter}
                  onChange={(e) => setTicketStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="ALL">All Status</option>
                  <option value="PENDING">PENDING</option>
                  <option value="PAID">PAID</option>
                </select>
              </div>
              <div>
                <label htmlFor="ticket-nationality-filter" className="block text-xs font-medium text-gray-700 mb-1">Nationality</label>
                <select
                  id="ticket-nationality-filter"
                  value={ticketNationalityFilter}
                  onChange={(e) => setTicketNationalityFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="ALL">All Nationality</option>
                  <option value="Taiwan">🇹🇼 Taiwan</option>
                  <option value="South Korea">🇰🇷 South Korea</option>
                  <option value="Japan">🇯🇵 Japan</option>
                  <option value="China">🇨🇳 China</option>
                  <option value="Hong Kong">🇭🇰 Hong Kong</option>
                  <option value="Singapore">🇸🇬 Singapore</option>
                  <option value="Malaysia">🇲🇾 Malaysia</option>
                  <option value="Thailand">🇹🇭 Thailand</option>
                  <option value="Vietnam">🇻🇳 Vietnam</option>
                  <option value="Philippines">🇵🇭 Philippines</option>
                  <option value="Indonesia">🇮🇩 Indonesia</option>
                  <option value="India">🇮🇳 India</option>
                  <option value="United States">🇺🇸 United States</option>
                  <option value="Canada">🇨🇦 Canada</option>
                  <option value="United Kingdom">🇬🇧 United Kingdom</option>
                  <option value="Germany">🇩🇪 Germany</option>
                  <option value="France">🇫🇷 France</option>
                  <option value="Spain">🇪🇸 Spain</option>
                  <option value="Italy">🇮🇹 Italy</option>
                  <option value="Netherlands">🇳🇱 Netherlands</option>
                  <option value="Sweden">🇸🇪 Sweden</option>
                  <option value="Norway">🇳🇴 Norway</option>
                  <option value="Denmark">🇩🇰 Denmark</option>
                  <option value="Finland">🇫🇮 Finland</option>
                  <option value="Switzerland">🇨🇭 Switzerland</option>
                  <option value="Austria">🇦🇹 Austria</option>
                  <option value="Belgium">🇧🇪 Belgium</option>
                  <option value="Australia">🇦🇺 Australia</option>
                  <option value="New Zealand">🇳🇿 New Zealand</option>
                  <option value="Brazil">🇧🇷 Brazil</option>
                  <option value="Argentina">🇦🇷 Argentina</option>
                  <option value="Mexico">🇲🇽 Mexico</option>
                  <option value="Chile">🇨🇱 Chile</option>
                  <option value="Colombia">🇨🇴 Colombia</option>
                  <option value="Peru">🇵🇪 Peru</option>
                  <option value="Venezuela">🇻🇪 Venezuela</option>
                  <option value="Russia">🇷🇺 Russia</option>
                  <option value="Ukraine">🇺🇦 Ukraine</option>
                  <option value="Poland">🇵🇱 Poland</option>
                  <option value="Czech Republic">🇨🇿 Czech Republic</option>
                  <option value="Hungary">🇭🇺 Hungary</option>
                  <option value="Romania">🇷🇴 Romania</option>
                  <option value="Bulgaria">🇧🇬 Bulgaria</option>
                  <option value="Croatia">🇭🇷 Croatia</option>
                  <option value="Serbia">🇷🇸 Serbia</option>
                  <option value="Slovenia">🇸🇮 Slovenia</option>
                  <option value="Slovakia">🇸🇰 Slovakia</option>
                  <option value="Estonia">🇪🇪 Estonia</option>
                  <option value="Latvia">🇱🇻 Latvia</option>
                  <option value="Lithuania">🇱🇹 Lithuania</option>
                  <option value="Turkey">🇹🇷 Turkey</option>
                  <option value="Israel">🇮🇱 Israel</option>
                  <option value="Saudi Arabia">🇸🇦 Saudi Arabia</option>
                  <option value="United Arab Emirates">🇦🇪 United Arab Emirates</option>
                  <option value="Egypt">🇪🇬 Egypt</option>
                  <option value="South Africa">🇿🇦 South Africa</option>
                  <option value="Nigeria">🇳🇬 Nigeria</option>
                  <option value="Kenya">🇰🇪 Kenya</option>
                  <option value="Morocco">🇲🇦 Morocco</option>
                  <option value="Tunisia">🇹🇳 Tunisia</option>
                  <option value="Algeria">🇩🇿 Algeria</option>
                  <option value="Ethiopia">🇪🇹 Ethiopia</option>
                  <option value="Ghana">🇬🇭 Ghana</option>
                  <option value="Other">🌍 Other</option>
                </select>
              </div>
              <div>
                <label htmlFor="ticket-payment-filter" className="block text-xs font-medium text-gray-700 mb-1">Payment</label>
                <select
                  id="ticket-payment-filter"
                  value={ticketPaymentFilter}
                  onChange={(e) => setTicketPaymentFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="ALL">All Payment</option>
                  <option value="Transfer">Transfer</option>
                  <option value="Cash">Cash</option>
                </select>
              </div>
            </div>

            {/* 티켓 목록 */}
            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Loading tickets...</p>
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No tickets found.</p>
              </div>
            ) : ticketViewMode === 'grouped' ? (
              /* 그룹핑된 뷰 */
              <div className="space-y-6">
                {groupedTickets.map((group, index) => (
                  <div key={`${group.eventId}-${index}`} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900">{group.eventTitle}</h3>
                      <div className="flex items-center gap-4 mt-1">
                        <span className="text-sm text-gray-600">Event ID: {group.eventId}</span>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          group.eventBranch === 'NCCU' ? 'bg-blue-100 text-blue-800' :
                          group.eventBranch === 'NTU' ? 'bg-purple-100 text-purple-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {group.eventBranch || '—'}
                        </span>
                        <span className="text-sm text-gray-600">{group.tickets.length} registrations</span>
                      </div>
                    </div>
                    {/* Desktop Table */}
                    <div className="hidden lg:block overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nationality</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {group.tickets.map((ticket, index) => (
                            <tr key={ticket.id} className="hover:bg-gray-50">
                              <td className="px-3 py-3 cursor-pointer hover:bg-blue-50" onClick={() => setSelectedUser(ticket)}>
                                <div className="text-sm font-medium text-gray-900">{ticket.userName || '—'}</div>
                                <div className="text-xs text-gray-500 mt-0.5">{ticket.userEmail || '—'}</div>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap">
                                <span className="text-xs">
                                  {ticket.nationality === 'Taiwan' ? '🇹🇼' : '🌍'} {ticket.nationality || '—'}
                                </span>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap">
                                <div className="text-sm font-semibold text-gray-900">{ticket.amount ? `${ticket.amount} NTD` : 'N/A'}</div>
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {ticket.paymentMethod === 'cash' ? '💵 Cash' : ticket.paymentMethod === 'transfer' ? '🏦 Transfer' : '—'}
                                </div>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  ticket.status === 'PAID' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {ticket.status}
                                </span>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap">
                                <div className="flex flex-col gap-1">
                                  {ticket.status === 'PENDING' ? (
                                    <button
                                      onClick={() => handleUpdateTicketStatus(ticket.id, 'PAID')}
                                      className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                                    >
                                      ✓ PAID
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => handleUpdateTicketStatus(ticket.id, 'PENDING')}
                                      className="px-2 py-1 bg-lime-500 text-white text-xs rounded hover:bg-lime-600"
                                    >
                                      ⏳ PENDING
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDeleteTicket(ticket.id)}
                                    className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                                  >
                                    🗑️ Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="lg:hidden space-y-3 p-3">
                      {group.tickets.map((ticket, index) => (
                        <div key={ticket.id} className="bg-gray-50 border border-gray-200 rounded-lg p-3 shadow-sm">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1" onClick={() => setSelectedUser(ticket)}>
                              <h5 className="font-semibold text-gray-900 text-sm">{ticket.userName || '—'}</h5>
                              <p className="text-xs text-gray-500 mt-0.5">{ticket.userEmail || '—'}</p>
                            </div>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              ticket.status === 'PAID' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {ticket.status}
                            </span>
                          </div>
                          
                          <div className="space-y-1 mb-2 text-xs" onClick={() => setSelectedUser(ticket)}>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Nationality:</span>
                              <span>{ticket.nationality === 'Taiwan' ? '🇹🇼' : '🌍'} {ticket.nationality || '—'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Payment:</span>
                              <span className="font-semibold">
                                {ticket.amount ? `${ticket.amount} NTD` : 'N/A'} 
                                ({ticket.paymentMethod === 'cash' ? '💵' : '🏦'})
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex gap-2 pt-2 border-t border-gray-200">
                            {ticket.status === 'PENDING' ? (
                              <button
                                onClick={() => handleUpdateTicketStatus(ticket.id, 'PAID')}
                                className="flex-1 px-2 py-1.5 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                              >
                                ✓ PAID
                              </button>
                            ) : (
                              <button
                                onClick={() => handleUpdateTicketStatus(ticket.id, 'PENDING')}
                                className="flex-1 px-2 py-1.5 bg-lime-500 text-white text-xs rounded hover:bg-lime-600"
                              >
                                ⏳ PENDING
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteTicket(ticket.id)}
                              className="px-2 py-1.5 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* 리스트 뷰 - Desktop & Mobile 반응형 */
              <>
                {/* Desktop Table View (hidden on mobile) */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => setTicketSortKey(ticketSortKey === 'name' ? 'name_desc' : 'name')}>
                          Name {ticketSortKey === 'name' ? '↑' : ticketSortKey === 'name_desc' ? '↓' : ''}
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nationality</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => setTicketSortKey(ticketSortKey === 'status' ? 'status_desc' : 'status')}>
                          Status {ticketSortKey === 'status' ? '↑' : ticketSortKey === 'status_desc' ? '↓' : ''}
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paginatedTickets.map((ticket, index) => (
                        <tr key={ticket.id} className="hover:bg-gray-50">
                          <td className="px-3 py-3 cursor-pointer hover:bg-blue-50" onClick={() => setSelectedUser(ticket)}>
                            <div className="text-sm font-semibold text-gray-900">{ticket.eventTitle || `Event #${ticket.eventId}`}</div>
                            <div className="text-xs text-gray-500 mt-0.5">{ticket.eventBranch || '—'}</div>
                          </td>
                          <td className="px-3 py-3 cursor-pointer hover:bg-blue-50" onClick={() => setSelectedUser(ticket)}>
                            <div className="text-sm font-medium text-gray-900">{ticket.userName || '—'}</div>
                            <div className="text-xs text-gray-500 mt-0.5">{ticket.userEmail || '—'}</div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <span className="text-xs">
                              {ticket.nationality === 'Taiwan' ? '🇹🇼' : '🌍'} {ticket.nationality || '—'}
                            </span>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <div className="text-sm font-semibold text-gray-900">{ticket.amount ? `${ticket.amount} NTD` : 'N/A'}</div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {ticket.paymentMethod === 'cash' ? '💵 Cash' : ticket.paymentMethod === 'transfer' ? '🏦 Transfer' : '—'}
                            </div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              ticket.status === 'PAID' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {ticket.status}
                            </span>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <div className="flex flex-col gap-1">
                              {ticket.status === 'PENDING' ? (
                                <button
                                  onClick={() => handleUpdateTicketStatus(ticket.id, 'PAID')}
                                  className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                                >
                                  ✓ PAID
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleUpdateTicketStatus(ticket.id, 'PENDING')}
                                  className="px-2 py-1 bg-lime-500 text-white text-xs rounded hover:bg-lime-600"
                                >
                                  ⏳ PENDING
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteTicket(ticket.id)}
                                className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                              >
                                🗑️ Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View (visible on mobile only) */}
                <div className="lg:hidden space-y-4">
                  {paginatedTickets.map((ticket, index) => (
                    <div key={ticket.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1" onClick={() => setSelectedUser(ticket)}>
                          <h4 className="font-semibold text-gray-900 text-sm">{ticket.eventTitle || `Event #${ticket.eventId}`}</h4>
                          <p className="text-xs text-gray-500 mt-0.5">{ticket.eventBranch || '—'}</p>
                        </div>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          ticket.status === 'PAID' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {ticket.status}
                        </span>
                      </div>
                      
                      <div className="space-y-2 mb-3" onClick={() => setSelectedUser(ticket)}>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Name:</span>
                          <span className="font-medium text-gray-900">{ticket.userName || '—'}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Nationality:</span>
                          <span className="text-xs">
                            {ticket.nationality === 'Taiwan' ? '🇹🇼' : '🌍'} {ticket.nationality || '—'}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Payment:</span>
                          <span className="font-semibold text-gray-900">
                            {ticket.amount ? `${ticket.amount} NTD` : 'N/A'}
                            <span className="text-xs text-gray-500 ml-1">
                              ({ticket.paymentMethod === 'cash' ? '💵 Cash' : '🏦 Transfer'})
                            </span>
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 pt-3 border-t border-gray-100">
                        {ticket.status === 'PENDING' ? (
                          <button
                            onClick={() => handleUpdateTicketStatus(ticket.id, 'PAID')}
                            className="flex-1 px-3 py-2 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 font-medium"
                          >
                            ✓ Mark as PAID
                          </button>
                        ) : (
                          <button
                            onClick={() => handleUpdateTicketStatus(ticket.id, 'PENDING')}
                            className="flex-1 px-3 py-2 bg-lime-500 text-white text-xs rounded-lg hover:bg-lime-600 font-medium"
                          >
                            ⏳ Mark as PENDING
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteTicket(ticket.id)}
                          className="px-3 py-2 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 font-medium"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* 페이지네이션 - 리스트 뷰에서만 표시 */}
            {ticketViewMode === 'list' && ticketTotalPages > 1 && (
              <div className="flex justify-center mt-6">
                <div className="flex space-x-2">
                  <button
                    onClick={() => setTicketCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={ticketCurrentPage === 1}
                    className="px-3 py-2 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  
                  {Array.from({ length: ticketTotalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setTicketCurrentPage(page)}
                      className={`px-3 py-2 text-sm border rounded-md ${
                        ticketCurrentPage === page 
                          ? 'bg-blue-600 text-white border-blue-600' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => setTicketCurrentPage(prev => Math.min(prev + 1, ticketTotalPages))}
                    disabled={ticketCurrentPage === ticketTotalPages}
                    className="px-3 py-2 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      </div>
      
      {/* Ticket User Detail Modal */}
      {selectedUser && activeTab === 'tickets' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 sm:p-8 rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl sm:text-2xl font-bold">Ticket Registration Details</h2>
              <button 
                onClick={() => setSelectedUser(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-1 gap-3">
                {/* Event Info */}
                <div className="p-3 bg-gray-50 rounded">
                  <p><strong>Event:</strong> {selectedUser.eventTitle || selectedUser.eventId}</p>
                  <p><strong>Branch:</strong> {selectedUser.eventBranch || 'N/A'}</p>
                </div>
                
                {/* User Info */}
                <div className="p-3 bg-gray-50 rounded">
                  <p><strong>Name:</strong> {selectedUser.userName || 'N/A'}</p>
                  <p><strong>Email:</strong> {selectedUser.userEmail || 'N/A'}</p>
                </div>
                
                {/* Registration Details */}
                <div className="p-3 bg-gray-50 rounded">
                  <p><strong>Nationality:</strong> {selectedUser.nationality || 'N/A'}</p>
                  <p><strong>Payment Method:</strong> {selectedUser.paymentMethod || 'N/A'}</p>
                  <p><strong>Amount:</strong> {selectedUser.amount === 0 ? 'FREE' : (selectedUser.amount ? `${selectedUser.amount} NTD` : 'N/A')}</p>
                  <p><strong>Status:</strong> 
                    <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      selectedUser.status === 'PAID' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {selectedUser.status}
                    </span>
                  </p>
                </div>
                
                {/* Dietary Preferences */}
                {selectedUser.foodAllergies && (
                  <div className="p-3 bg-gray-50 rounded">
                    <p><strong>Dietary Preferences:</strong> {selectedUser.foodAllergies}</p>
                  </div>
                )}
                
                {/* How did you hear about SLAM */}
                {selectedUser.howDidYouHear && (
                  <div className="p-3 bg-gray-50 rounded">
                    <p><strong>How did you hear about SLAM:</strong> {selectedUser.howDidYouHear}</p>
                  </div>
                )}
                
                {/* Dates */}
                <div className="p-3 bg-gray-50 rounded">
                  <p><strong>Registered:</strong> {selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleDateString() : 'N/A'}</p>
                  {selectedUser.paidAt && (
                    <p><strong>Paid:</strong> {new Date(selectedUser.paidAt).toLocaleDateString()}</p>
                  )}
                </div>
                
                {/* Special Requests */}
                {selectedUser.specialRequests && (
                  <div className="p-3 bg-gray-50 rounded">
                    <p><strong>Special Requests:</strong></p>
                    <p className="mt-1 text-gray-700">{selectedUser.specialRequests}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
